import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Empty, Modal, Popconfirm, Spin, Tag, Typography, message } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, QrcodeOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import DeliveryFormFields from "../../components/delivery/DeliveryFormFields";
import DeliverySharePanel from "../../components/delivery/DeliverySharePanel";
import copyToClipboard from "../../utils/copy";
import { buildDeliveryUrl } from "../../utils/deliveryToken";
import { deleteDelivery, listDeliveries, updateDelivery } from "../../utils/deliveryAdminApi";
import { deliveryToFormValues, validateDeliveryForm } from "../../utils/deliveryForm";
import { useTranslation } from "../../i18n";
import "./delivery.css";

const { Title, Paragraph, Text } = Typography;

function DeliveriesManage() {
    const { t } = useTranslation();
    const [deliveries, setDeliveries] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [editing, setEditing] = useState(null);
    const [posterItem, setPosterItem] = useState(null);
    const [editError, setEditError] = useState("");
    const [saving, setSaving] = useState(false);

    const loadDeliveries = useCallback(async () => {
        setLoadingList(true);
        try {
            const items = await listDeliveries();
            setDeliveries(items);
        } catch {
            message.error(t("delivery.admin.manageLoadFailed"));
        } finally {
            setLoadingList(false);
        }
    }, [t]);

    useEffect(() => {
        loadDeliveries();
    }, [loadDeliveries]);

    const handleCopy = useCallback(
        async (id) => {
            try {
                await copyToClipboard(buildDeliveryUrl(id));
                message.success(t("delivery.admin.linkCopied"));
            } catch {
                message.error(t("delivery.admin.copyFailed"));
            }
        },
        [t]
    );

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteDelivery(id);
                message.success(t("delivery.admin.deleteDone"));
                loadDeliveries();
            } catch {
                message.error(t("delivery.admin.deleteFailed"));
            }
        },
        [loadDeliveries, t]
    );

    const openEdit = useCallback((item) => {
        setEditError("");
        setEditing({ id: item.id, item, values: deliveryToFormValues(item) });
    }, []);

    const handleEditField = useCallback((field, value) => {
        setEditing((prev) =>
            prev ? { ...prev, values: { ...prev.values, [field]: value } } : prev
        );
    }, []);

    const handleSave = useCallback(async () => {
        if (!editing) {
            return;
        }
        setEditError("");

        let normalized;
        try {
            normalized = validateDeliveryForm(editing.values, t, {
                allowExpiredExpiry: Boolean(editing.item?.expired),
            });
        } catch (err) {
            setEditError(err.message || t("delivery.admin.editFailed"));
            return;
        }

        setSaving(true);
        try {
            await updateDelivery(editing.id, {
                albumPath: normalized.albumPath,
                albumName: normalized.albumName,
                expiry: normalized.expiry,
                consent: normalized.consent,
                type: normalized.type,
                downloadAllType: normalized.downloadAllType,
                downloadAllToken: normalized.downloadAllToken,
                archiveMarkdown: normalized.archiveMarkdown,
            });
            message.success(t("delivery.admin.editDone"));
            setEditing(null);
            loadDeliveries();
        } catch (err) {
            setEditError(err.message || t("delivery.admin.editFailed"));
        } finally {
            setSaving(false);
        }
    }, [editing, loadDeliveries, t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("delivery.admin.manageTitle")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("delivery.admin.manageSubtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    {loadingList ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : deliveries.length === 0 ? (
                        <Empty description={t("delivery.admin.manageEmpty")} />
                    ) : (
                        <div className="delivery-admin-list">
                            {deliveries.map((item) => (
                                <div key={item.id} className="delivery-admin-list__item">
                                    <div className="delivery-admin-list__main">
                                        <div className="delivery-admin-list__title">
                                            <Text strong>
                                                {item.albumName ||
                                                    t("delivery.actions.defaultAlbumName")}
                                            </Text>
                                            <Tag color={item.expired ? "default" : "green"}>
                                                {item.expired
                                                    ? t("delivery.admin.expiredTag")
                                                    : t("delivery.admin.validTag")}
                                            </Tag>
                                            {item.downloadAll && (
                                                <Tag color="blue">
                                                    {item.downloadAll.type === "gdrive"
                                                        ? t("delivery.admin.downloadAllGdrive")
                                                        : t("delivery.admin.downloadAllZip")}
                                                </Tag>
                                            )}
                                        </div>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {item.albumPath}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("delivery.admin.expiresAt", {
                                                time: dayjs(item.expiry * 1000).format(
                                                    "YYYY-MM-DD HH:mm"
                                                ),
                                            })}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("delivery.admin.viewCount", {
                                                count: item.viewCount ?? 0,
                                            })}
                                        </Text>
                                    </div>
                                    <div className="delivery-admin-list__actions">
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit(item)}
                                        >
                                            {t("delivery.admin.edit")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopy(item.id)}
                                        >
                                            {t("delivery.admin.copyLink")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<QrcodeOutlined />}
                                            onClick={() => setPosterItem(item)}
                                        >
                                            {t("delivery.admin.getPoster")}
                                        </Button>
                                        <Popconfirm
                                            title={t("delivery.admin.deleteConfirm")}
                                            okText={t("delivery.admin.deleteOk")}
                                            cancelText={t("delivery.admin.deleteCancel")}
                                            onConfirm={() => handleDelete(item.id)}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={Boolean(editing)}
                title={t("delivery.admin.editTitle")}
                onCancel={() => !saving && setEditing(null)}
                footer={[
                    <Button key="cancel" disabled={saving} onClick={() => setEditing(null)}>
                        {t("delivery.admin.deleteCancel")}
                    </Button>,
                    <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                        {t("delivery.admin.save")}
                    </Button>,
                ]}
                width={560}
                destroyOnClose
            >
                {editing && (
                    <>
                        <Paragraph type="secondary" style={{ fontSize: "0.82rem" }}>
                            ID: {editing.id}
                        </Paragraph>
                        <DeliveryFormFields values={editing.values} onChange={handleEditField} />
                        {editError && <Paragraph type="danger">{editError}</Paragraph>}
                    </>
                )}
            </Modal>

            <Modal
                open={Boolean(posterItem)}
                title={t("delivery.admin.posterTitle")}
                onCancel={() => setPosterItem(null)}
                footer={null}
                width={560}
                destroyOnClose
            >
                {posterItem && (
                    <>
                        <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                            {t("delivery.admin.posterHint")}
                        </Paragraph>
                        <DeliverySharePanel
                            url={buildDeliveryUrl(posterItem.id)}
                            albumName={
                                posterItem.albumName || t("delivery.actions.defaultAlbumName")
                            }
                            expiresAtLabel={dayjs(posterItem.expiry * 1000).format(
                                "YYYY-MM-DD HH:mm"
                            )}
                            compact
                        />
                    </>
                )}
            </Modal>

            <Footer />
        </>
    );
}

export default DeliveriesManage;
